import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { t, useI18n } from './i18n';
import { useNavigate } from 'react-router-dom';
import './ProactiveScenariosPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://b.supchats.com';

const ProactiveSelect = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selected = options.find((opt) => opt.value === value);

  return (
    <div className="proactive-select-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="proactive-select-trigger"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="proactive-select-trigger-label">
          {selected ? selected.label : ''}
        </span>
        <span className="proactive-select-trigger-arrow">▾</span>
      </button>
      {open && (
        <div className="proactive-select-dropdown">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={
                'proactive-select-option' +
                (opt.value === value ? ' proactive-select-option-selected' : '')
              }
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProactiveScenariosPage = () => {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const emptyScenario = {
    id: null,
    name: '',
    audience: 'all',
    attributes: '',
    attribute_condition: 'any',
    message_text: '',
    attribute_message_text: '',
    schedule_type: 'immediate',
    start_at: '',
    end_at: '',
    limit_type: 'once',
    max_impressions: '',
    is_active: true,
  };

  const userToken = () => localStorage.getItem('token');

  const fetchAgents = async () => {
    try {
      const token = userToken();
      if (!token) return;
      const res = await axios.get(`${API_URL}/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgents(res.data || []);
      if (res.data && res.data.length && !selectedAgentId) {
        setSelectedAgentId(res.data[0].id);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        navigate('/');
        return;
      }
      setError(t('Failed to load agents.'));
    }
  };

  const fetchScenarios = async (agentId) => {
    if (!agentId) return;
    setLoading(true);
    setError('');
    try {
      const token = userToken();
      if (!token) return;
      const res = await axios.get(`${API_URL}/agents/${agentId}/proactive-scenarios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScenarios(res.data || []);
      setSelectedIds([]);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        navigate('/');
        return;
      }
      setError(t('Failed to load scenarios.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedAgentId) {
      fetchScenarios(selectedAgentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentId]);

  const handleEditField = (field, value) => {
    setEditingScenario((prev) => ({ ...(prev || emptyScenario), [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedAgentId || !editingScenario) return;

    const scenario = editingScenario || emptyScenario;
    if (!scenario.name.trim()) {
      setError(t('Please enter a scenario name.'));
      return;
    }
    if (!scenario.message_text && scenario.audience !== 'has_attribute') {
      setError(t('Please enter a message text.'));
      return;
    }
    if (
      scenario.audience === 'has_attribute' &&
      (!scenario.attributes || !scenario.attribute_message_text)
    ) {
      setError(t('Please specify attributes and message text for the attribute-based option.'));
      return;
    }

    const limitType = scenario.limit_type || 'once';
    let maxImpressions = null;
    if (limitType === 'count') {
      const parsed = parseInt(scenario.max_impressions, 10);
      if (!parsed || parsed <= 0) {
        setError(t('Please enter a valid impressions count.'));
        return;
      }
      maxImpressions = parsed;
    }

    const payload = {
      name: scenario.name,
      audience: scenario.audience,
      attributes: scenario.attributes || null,
      attribute_condition: scenario.attribute_condition || 'any',
      message_text: scenario.audience === 'has_attribute' ? null : scenario.message_text,
      attribute_message_text:
        scenario.audience === 'has_attribute' ? scenario.attribute_message_text : null,
      schedule_type: scenario.schedule_type || 'immediate',
      start_at:
        (scenario.schedule_type === 'scheduled' || limitType === 'date_range') &&
        scenario.start_at
          ? new Date(scenario.start_at).toISOString()
          : null,
      end_at:
        limitType === 'date_range' && scenario.end_at
          ? new Date(scenario.end_at).toISOString()
          : null,
      limit_type: limitType,
      max_impressions: maxImpressions,
      is_active: scenario.is_active !== false,
    };

    try {
      const token = userToken();
      if (!token) return;
      if (scenario.id) {
        await axios.put(
          `${API_URL}/agents/${selectedAgentId}/proactive-scenarios/${scenario.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/agents/${selectedAgentId}/proactive-scenarios`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setEditingScenario(null);
      fetchScenarios(selectedAgentId);
      setError('');
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        navigate('/');
        return;
      }
      setError(t('Failed to save scenario.'));
    }
  };

  const handleDelete = async (id) => {
    if (!selectedAgentId) return;
    if (!window.confirm(t('Delete this scenario permanently?'))) return;
    try {
      const token = userToken();
      if (!token) return;
      await axios.delete(
        `${API_URL}/agents/${selectedAgentId}/proactive-scenarios/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchScenarios(selectedAgentId);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        navigate('/');
        return;
      }
      setError(t('Failed to delete scenario.'));
    }
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(scenarios.map((s) => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const handleBulkStatusChange = async (isActive) => {
    if (!selectedAgentId || selectedIds.length === 0) return;
    try {
      const token = userToken();
      if (!token) return;
      await Promise.all(
        selectedIds.map((id) =>
          axios.put(
            `${API_URL}/agents/${selectedAgentId}/proactive-scenarios/${id}`,
            { is_active: isActive },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setSelectedIds([]);
      fetchScenarios(selectedAgentId);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        navigate('/');
        return;
      }
      setError(t('Failed to update scenarios.'));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedAgentId || selectedIds.length === 0) return;
    if (!window.confirm(t('Delete selected scenarios permanently?'))) return;
    try {
      const token = userToken();
      if (!token) return;
      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(
            `${API_URL}/agents/${selectedAgentId}/proactive-scenarios/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setSelectedIds([]);
      fetchScenarios(selectedAgentId);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        navigate('/');
        return;
      }
      setError(t('Failed to delete scenarios.'));
    }
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case 'all':
        return t('All users');
      case 'new':
        return t('New users');
      case 'old':
        return t('Returning users');
      case 'has_attribute':
        return t('With attribute');
      case 'no_attribute':
        return t('Without attribute');
      default:
        return audience || '';
    }
  };

  const selectedAgent = agents.find((a) => a.id === selectedAgentId) || null;

  return (
    <div className="proactive-page">
      <div className="proactive-header">
        <h2>{t('Proactive scenarios')}</h2>
        <div className="proactive-controls">
          <select
            className="proactive-select"
            value={selectedAgentId || ''}
            onChange={(e) => setSelectedAgentId(Number(e.target.value) || null)}
          >
            {agents.length === 0 && <option value="">{t('No agents')}</option>}
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name || t('Agent #%(id)s', { id: agent.id })}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="proactive-refresh-btn"
            onClick={() => selectedAgentId && fetchScenarios(selectedAgentId)}
            disabled={loading || !selectedAgentId}
          >
            {t('Refresh')}
          </button>
          <button
            type="button"
            className="proactive-new-btn"
            onClick={() => setEditingScenario({ ...emptyScenario, id: null })}
            disabled={!selectedAgentId}
          >
            {t('Add scenario')}
          </button>
        </div>
      </div>

      {selectedAgent && (
        <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
          {t('Configure proactive messages for agent %(name)s.', {
            name: selectedAgent.name,
          })}
        </p>
      )}

      {error && <div className="proactive-error">{error}</div>}

      <div className="proactive-bulk-actions">
        <span className="proactive-bulk-label">
          {t('Selected: %(count)s', { count: selectedIds.length })}
        </span>
        <button
          type="button"
          className="proactive-bulk-btn"
          disabled={selectedIds.length === 0}
          onClick={() => handleBulkStatusChange(true)}
        >
          {t('Enable selected')}
        </button>
        <button
          type="button"
          className="proactive-bulk-btn"
          disabled={selectedIds.length === 0}
          onClick={() => handleBulkStatusChange(false)}
        >
          {t('Disable selected')}
        </button>
        <button
          type="button"
          className="proactive-bulk-btn danger"
          disabled={selectedIds.length === 0}
          onClick={handleBulkDelete}
        >
          {t('Delete selected')}
        </button>
      </div>

      <div className="proactive-table-wrapper">
        <table className="proactive-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    scenarios.length > 0 &&
                    selectedIds.length === scenarios.length
                  }
                  onChange={(e) => handleToggleSelectAll(e.target.checked)}
                />
              </th>
              <th>{t('Name')}</th>
              <th>{t('Audience')}</th>
              <th>{t('Start')}</th>
              <th>{t('Limit')}</th>
              <th>{t('Active')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {scenarios.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="proactive-empty">
                  {t('No scenarios yet.')}
                </td>
              </tr>
            )}
            {scenarios.map((s) => (
              <tr key={s.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(s.id)}
                    onChange={(e) => handleToggleSelect(s.id, e.target.checked)}
                  />
                </td>
                <td>{s.name}</td>
                <td>{getAudienceLabel(s.audience)}</td>
                <td>
                  {s.schedule_type === 'immediate'
                    ? t('Immediate')
                    : s.start_at
                    ? new Date(s.start_at).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US')
                    : '—'}
                </td>
                <td>
                  {s.limit_type === 'once'
                    ? t('Once')
                    : s.limit_type === 'count'
                    ? t('%(count)s times', { count: s.max_impressions || 0 })
                    : s.limit_type === 'date_range'
                    ? t('Date range')
                    : '—'}
                </td>
                <td>
                  <span
                    className={
                      s.is_active ? 'proactive-status-active' : 'proactive-status-inactive'
                    }
                  >
                    {s.is_active ? t('Yes') : t('No')}
                  </span>
                </td>
                <td className="proactive-actions-cell">
                  <button
                    type="button"
                    className="proactive-link-button"
                    onClick={() =>
                      setEditingScenario({
                        id: s.id,
                        name: s.name,
                        audience: s.audience,
                        attributes: s.attributes || '',
                        attribute_condition: s.attribute_condition || 'any',
                        message_text: s.message_text || '',
                        attribute_message_text: s.attribute_message_text || '',
                        schedule_type: s.schedule_type || 'immediate',
                        start_at: s.start_at
                          ? new Date(s.start_at).toISOString().slice(0, 16)
                          : '',
                        end_at: s.end_at
                          ? new Date(s.end_at).toISOString().slice(0, 16)
                          : '',
                        limit_type: s.limit_type || 'once',
                        max_impressions: s.max_impressions ?? '',
                        is_active: s.is_active,
                      })
                    }
                  >
                    {t('Edit')}
                  </button>
                  <button
                    type="button"
                    className="proactive-link-button danger"
                    onClick={() => handleDelete(s.id)}
                  >
                    {t('Delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingScenario && (
        <div
          className="proactive-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingScenario(null);
            }
          }}
        >
          <div className="proactive-modal">
            <div className="proactive-modal-header">
              <h3>
                {editingScenario.id ? t('Edit scenario') : t('New scenario')}
              </h3>
            </div>
            <div className="proactive-modal-body">
              <div className="proactive-field">
                <label>
                  {t('Name')}:
                  <span className="tooltip proactive-tooltip tooltip-below-left">
                    <span className="help-icon">?</span>
                    <span className="tooltiptext">
                      {t(
                        'Internal scenario name to help you identify it in the list. For example: Returning visit, Exit intent, Black Friday offer.'
                      )}
                    </span>
                  </span>
                </label>
                <input
                  type="text"
                  value={editingScenario.name}
                  onChange={(e) => handleEditField('name', e.target.value)}
                  placeholder={t('For example: Returning visit, Exit intent...')}
                />
              </div>

              <div className="proactive-field">
                <label>
                  {t('Audience')}:
                  <span className="tooltip proactive-tooltip tooltip-below-right">
                    <span className="help-icon">?</span>
                    <span className="tooltiptext">
                      {t(
                        'Who should see this message: all visitors, only new visitors, returning visitors, or visitors with/without specific attributes.'
                      )}
                    </span>
                  </span>
                </label>
                <ProactiveSelect
                  value={editingScenario.audience}
                  onChange={(val) => handleEditField('audience', val)}
                  options={[
                    { value: 'all', label: t('All users') },
                    { value: 'new', label: t('New users') },
                    { value: 'old', label: t('Returning users') },
                    { value: 'has_attribute', label: t('With attribute') },
                    { value: 'no_attribute', label: t('Without attribute') },
                  ]}
                />
              </div>

              {editingScenario.audience === 'has_attribute' && (
                <>
                  <div className="proactive-field">
                    <label>
                      {t('Attributes (comma separated)')}:
                      <span className="tooltip proactive-tooltip tooltip-below-left">
                        <span className="help-icon">?</span>
                        <span className="tooltiptext">
                          {t(
                            'List of attribute tokens that must be present in the user attributes (client_uid). For example: matuha, plan=premium, segment_A.'
                          )}
                        </span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={editingScenario.attributes}
                      onChange={(e) => handleEditField('attributes', e.target.value)}
                      placeholder="attribute1, attribute2"
                    />
                  </div>
                  <div className="proactive-field" style={{ gridColumn: '1 / -1' }}>
                    <label>
                      {t('Message text when attributes match')}:
                      <span className="tooltip proactive-tooltip">
                        <span className="help-icon">?</span>
                        <span className="tooltiptext">
                          {t(
                            'The message that will be shown only to visitors whose attributes match the specified tokens.'
                          )}
                        </span>
                      </span>
                    </label>
                    <textarea
                      rows="3"
                      value={editingScenario.attribute_message_text}
                      onChange={(e) =>
                        handleEditField('attribute_message_text', e.target.value)
                      }
                      placeholder={t('Message that will be sent when attributes match.')}
                    />
                  </div>
                </>
              )}

              {editingScenario.audience !== 'has_attribute' && (
                <div className="proactive-field" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    {t('Message text')}:
                    <span className="tooltip proactive-tooltip tooltip-below-right-outside">
                      <span className="help-icon">?</span>
                      <span className="tooltiptext">
                        {t('The proactive message that will be sent to the selected audience.')}
                      </span>
                    </span>
                  </label>
                  <textarea
                    rows="3"
                    value={editingScenario.message_text}
                    onChange={(e) => handleEditField('message_text', e.target.value)}
                    placeholder={t('This text will be sent as a proactive message.')}
                  />
                </div>
              )}

              <div className="proactive-field">
                <label>
                  {t('Start')}:
                  <span className="tooltip proactive-tooltip tooltip-below-left">
                    <span className="help-icon">?</span>
                    <span className="tooltiptext">
                      {t(
                        'When to send this message: immediately after the visitor opens the site or at a specific scheduled time.'
                      )}
                    </span>
                  </span>
                </label>
                <div>
                  <label>
                    <input
                      type="radio"
                      name="proactive-start-admin"
                      checked={editingScenario.schedule_type === 'immediate'}
                      onChange={() => handleEditField('schedule_type', 'immediate')}
                    />{' '}
                    {t('Immediately')}
                  </label>
                </div>
                <div>
                  <label>
                    <input
                      type="radio"
                      name="proactive-start-admin"
                      checked={editingScenario.schedule_type === 'scheduled'}
                      onChange={() => handleEditField('schedule_type', 'scheduled')}
                    />{' '}
                    {t('Scheduled')}
                  </label>
                </div>
                {editingScenario.schedule_type === 'scheduled' && (
                  <input
                    type="datetime-local"
                    value={editingScenario.start_at}
                    onChange={(e) => handleEditField('start_at', e.target.value)}
                  />
                )}
              </div>

              <div className="proactive-field">
                <label>
                  {t('Limit type')}:
                  <span className="tooltip proactive-tooltip tooltip-below-right">
                    <span className="help-icon">?</span>
                    <span className="tooltiptext">
                      {t(
                        'How many times each visitor can see this message: once, a fixed number of times, or only within a selected date range.'
                      )}
                    </span>
                  </span>
                </label>
                <ProactiveSelect
                  value={editingScenario.limit_type}
                  onChange={(val) => handleEditField('limit_type', val)}
                  options={[
                    { value: 'once', label: t('Once') },
                    { value: 'count', label: t('N times') },
                    { value: 'date_range', label: t('By dates') },
                  ]}
                />
                {editingScenario.limit_type === 'count' && (
                  <input
                    type="number"
                    min="1"
                    value={editingScenario.max_impressions}
                    onChange={(e) => handleEditField('max_impressions', e.target.value)}
                    placeholder={t('For example, 3 times')}
                  />
                )}
                {editingScenario.limit_type === 'date_range' && (
                  <>
                    <input
                      type="datetime-local"
                      value={editingScenario.start_at}
                      onChange={(e) => handleEditField('start_at', e.target.value)}
                    />
                    <input
                      type="datetime-local"
                      value={editingScenario.end_at}
                      onChange={(e) => handleEditField('end_at', e.target.value)}
                    />
                  </>
                )}
              </div>

              <div className="proactive-field">
                <label>
                  {t('Status')}:
                  <span className="tooltip proactive-tooltip tooltip-top-right">
                    <span className="help-icon">?</span>
                    <span className="tooltiptext">
                      {t(
                        'Whether this scenario is active. Inactive scenarios are ignored and not sent to visitors.'
                      )}
                    </span>
                  </span>
                </label>
                <ProactiveSelect
                  value={editingScenario.is_active ? 'true' : 'false'}
                  onChange={(val) => handleEditField('is_active', val === 'true')}
                  options={[
                    { value: 'true', label: t('Enabled') },
                    { value: 'false', label: t('Disabled') },
                  ]}
                />
              </div>
            </div>

            <div className="proactive-modal-footer">
              <button
                type="button"
                className="proactive-cancel-btn"
                onClick={() => setEditingScenario(null)}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="proactive-save-btn"
                onClick={handleSave}
                disabled={loading}
              >
                {t('Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProactiveScenariosPage;


